import { fetchAuthSession } from "aws-amplify/auth";
import { get, post, put, del } from "@aws-amplify/api";
import { ApiGatewayConfig } from "@amzn/afxtaco-types";
import { config } from "../../config";

/**
 * Options for API requests
 */
interface ApiRequestOptions {
  headers?: Record<string, string>;
  body?: unknown;
  queryParams?: Record<string, string>;
}

/**
 * Retrieves the authentication token from the current session
 *
 * @returns Promise resolving to the authentication token
 * @throws Error if no authentication token is available
 */
export async function getAuthToken(): Promise<string> {
  const session = await fetchAuthSession();
  const token = session.tokens?.accessToken?.toString();

  if (!token) {
    throw new Error("No authentication token available");
  }

  return token;
}

/**
 * Creates default headers for API requests with authentication
 *
 * @param token - The authentication token
 * @param additionalHeaders - Optional additional headers to include
 * @returns Headers object with Authorization and any additional headers
 */
export function createAuthHeaders(
  token: string,
  additionalHeaders?: Record<string, string>,
): Record<string, string> {
  return {
    Authorization: `Bearer ${token}`,
    ...additionalHeaders,
  };
}

/**
 * Creates default options for API requests with authentication and credentials
 *
 * @param token - The authentication token
 * @param options - Optional additional options (headers, body, etc.)
 * @returns Options object configured for authenticated API requests
 */
export function createApiOptions(
  token: string,
  options?: ApiRequestOptions,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any {
  const baseOptions = {
    headers: createAuthHeaders(token, options?.headers),
    withCredentials: true,
  };

  if (options?.body) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (baseOptions as any).body = JSON.parse(JSON.stringify(options.body));
  }

  return baseOptions;
}

/**
 * Makes an authenticated GET request to the API Gateway
 *
 * @param path - The API path (e.g., "/projects/123")
 * @param options - Optional request options
 * @returns Promise resolving to the API response
 * @throws Error if authentication fails or request fails
 *
 * @example
 * ```typescript
 * const response = await apiGet("/projects/123");
 * const data = await response.body.json();
 * ```
 */
export async function apiGet(path: string, options?: ApiRequestOptions) {
  const apiGatewayConfig: ApiGatewayConfig = config.getApiGatewayConfig();
  const token = await getAuthToken();

  let fullPath = path;
  if (options?.queryParams) {
    const queryString = new URLSearchParams(options.queryParams).toString();
    fullPath += `?${queryString}`;
  }

  return get({
    apiName: apiGatewayConfig.name,
    path: fullPath,
    options: createApiOptions(token, options),
  });
}

/**
 * Makes an authenticated POST request to the API Gateway
 *
 * @param path - The API path (e.g., "/projects")
 * @param body - The request body
 * @param options - Optional request options
 * @returns Promise resolving to the API response
 * @throws Error if authentication fails or request fails
 *
 * @example
 * ```typescript
 * const response = await apiPost("/projects", { name: "My Project" });
 * const data = await response.body.json();
 * ```
 */
export async function apiPost(
  path: string,
  body: unknown,
  options?: ApiRequestOptions,
) {
  const apiGatewayConfig: ApiGatewayConfig = config.getApiGatewayConfig();
  const token = await getAuthToken();

  return post({
    apiName: apiGatewayConfig.name,
    path,
    options: createApiOptions(token, {
      ...options,
      body,
    }),
  });
}

/**
 * Makes an authenticated PUT request to the API Gateway
 *
 * @param path - The API path (e.g., "/projects/123")
 * @param body - The request body
 * @param options - Optional request options
 * @returns Promise resolving to the API response
 * @throws Error if authentication fails or request fails
 *
 * @example
 * ```typescript
 * const response = await apiPut("/projects/123", { name: "Updated Name" });
 * const data = await response.body.json();
 * ```
 */
export async function apiPut(
  path: string,
  body: unknown,
  options?: ApiRequestOptions,
) {
  const apiGatewayConfig: ApiGatewayConfig = config.getApiGatewayConfig();
  const token = await getAuthToken();

  return put({
    apiName: apiGatewayConfig.name,
    path,
    options: createApiOptions(token, {
      ...options,
      body,
    }),
  });
}

/**
 * Makes an authenticated DELETE request to the API Gateway
 *
 * @param path - The API path (e.g., "/projects/123")
 * @param options - Optional request options
 * @returns Promise resolving to the API response
 * @throws Error if authentication fails or request fails
 *
 * @example
 * ```typescript
 * const response = await apiDelete("/projects/123");
 * const data = await response.body.json();
 * ```
 */
export async function apiDelete(path: string, options?: ApiRequestOptions) {
  const apiGatewayConfig: ApiGatewayConfig = config.getApiGatewayConfig();
  const token = await getAuthToken();

  return del({
    apiName: apiGatewayConfig.name,
    path,
    options: createApiOptions(token, options),
  });
}
